import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getVolcanDescripcionById,
  getLineaTiempoPorVolcan,
  getDocumentosPorVolcan,
  getCartografiaHistoricaPorVolcan,
  getMapasAmenazaPorVolcan,
} from "../services/volcanService";
import VolcanSidebar from "../components/menu/VolcanSidebar";
import VolcanDescripcion from "../components/descripcion/VolcanDescripcion";
import VolcanDocumentos from "../components/documentos/VolcanDocumentos";
import VolcanCartografiaHistorica from "../components/cartografia_historica/VolcanCartografiaHistorica";
import VolcanMapasAmenaza from "../components/mapas_amenaza/VolcanMapasAmenaza";
import "./VolcanDetail.css";
import VolcanLineaTiempo from "../components/linea_tiempo/VolcanLineaTiempo";
import { useVolcanSidebarLayout } from "../../hooks/useVolcanSidebarLayout";

const VOLCAN_DETAIL_TAB_KEYS = new Set([
  "descripcion",
  "timeline",
  "documentos",
  "cartografia_historica",
  "mapas_amenaza",
]);

function VolcanDetail() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const tabParam = searchParams.get("tab");
  const eventoIdParam = searchParams.get("eventoId");
  const timelineEventParam = searchParams.get("timelineEvent");
  const navigate = useNavigate();

  const [volcan, setVolcan] = useState(null);
  const [activeTab, setActiveTab] = useState("descripcion");
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return !window.matchMedia("(max-width: 768px)").matches;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const bodyScrollRef = useRef(null);

  useVolcanSidebarLayout(sidebarOpen);

  useEffect(() => {
    window.scrollTo(0, 0);
    bodyScrollRef.current?.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!id || !eventoIdParam) return;
    const volcanId = Number(id);
    const eventoId = Number(eventoIdParam);
    if (!volcanId || !eventoId) return;
    navigate(`/evento-detalle?volcanId=${volcanId}&eventoId=${eventoId}`, {
      replace: true,
    });
  }, [id, eventoIdParam, navigate]);

  useEffect(() => {
    if (!id) return;
    const numericId = Number(id);
    if (!numericId) return;

    const te =
      timelineEventParam != null ? String(timelineEventParam).trim() : "";
    if (te && /^\d+$/.test(te)) {
      setActiveTab("timeline");
      return;
    }

    if (tabParam && VOLCAN_DETAIL_TAB_KEYS.has(tabParam)) {
      setActiveTab(tabParam);
      return;
    }

    setActiveTab("descripcion");
  }, [id, tabParam, timelineEventParam]);

  const handleTabChange = useCallback(
    (tabKey) => {
      if (!id) return;
      if (!VOLCAN_DETAIL_TAB_KEYS.has(tabKey)) return;

      setActiveTab(tabKey);

      const next = new URLSearchParams(searchParams);
      next.delete("eventoId");
      next.delete("timelineEvent");
      next.set("id", String(id));

      if (tabKey === "descripcion") {
        next.delete("tab");
      } else {
        next.set("tab", tabKey);
      }

      navigate(`/volcan-detalle?${next.toString()}`);
    },
    [id, navigate, searchParams],
  );

  useEffect(() => {
    if (!id) return;

    async function fetchData() {
      try {
        const volcanId = Number(id);

        const [
          descripcion,
          timeline,
          documentos,
          cartografia_historica,
          mapas_amenaza,
        ] = await Promise.all([
          getVolcanDescripcionById(volcanId),
          getLineaTiempoPorVolcan(volcanId),
          getDocumentosPorVolcan(volcanId),
          getCartografiaHistoricaPorVolcan(volcanId),
          getMapasAmenazaPorVolcan(volcanId),
        ]);

        const title =
          descripcion?.title ??
          descripcion?.volcan?.title ??
          descripcion?.volcan_title ??
          "";

        setVolcan({
          ...descripcion,
          volcan: { title },
          timeline: Array.isArray(timeline)
            ? timeline
            : (timeline?.results ?? []),
          documentos: Array.isArray(documentos)
            ? documentos
            : (documentos?.results ?? []),
          cartografia_historica: Array.isArray(cartografia_historica)
            ? cartografia_historica
            : (cartografia_historica?.results ?? []),
          mapas_amenaza: Array.isArray(mapas_amenaza)
            ? mapas_amenaza
            : (mapas_amenaza?.results ?? []),
        });
      } catch (err) {
        setError("No se pudo cargar el volcán");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="volcan-detail volcan-detail--solo-mensaje">
        <p>Cargando...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="volcan-detail volcan-detail--solo-mensaje">
        <p>{error}</p>
      </div>
    );
  }
  if (!volcan) {
    return (
      <div className="volcan-detail volcan-detail--solo-mensaje">
        <p>No existe el volcán</p>
      </div>
    );
  }

  const renderContenido = () => {
    switch (activeTab) {
      case "descripcion":
        return <VolcanDescripcion volcan={volcan} />;
      case "documentos":
        return <VolcanDocumentos documentos={volcan.documentos} />;
      case "cartografia_historica":
        return (
          <VolcanCartografiaHistorica
            cartografia_historica={volcan.cartografia_historica}
          />
        );
      case "mapas_amenaza":
        return <VolcanMapasAmenaza mapas_amenaza={volcan.mapas_amenaza} />;
      case "timeline":
        return (
          <VolcanLineaTiempo
            timeline={volcan.timeline || []}
            volcanId={Number(id)}
            volcanNombre={volcan?.volcan?.title ?? ""}
            initialTimelineEventId={
              timelineEventParam &&
              /^\d+$/.test(String(timelineEventParam).trim())
                ? Number(timelineEventParam)
                : undefined
            }
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="volcan-detail">
      <div className="volcan-layout">
        <VolcanSidebar
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />

        <div
          className={`volcan-content ${sidebarOpen ? "expanded" : "collapsed"}`}
        >
          <div className="volcan-body-scroll" ref={bodyScrollRef}>
            <div className="volcan-title volcan-title--sticky">
              <h2>{volcan.volcan.title}</h2>
            </div>
            <div className="volcan-body-inner">{renderContenido()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VolcanDetail;
